import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { startToolOperation, trackToolRejection, type ToolAnalyticsContext } from '@saas-pages/shared/public/runtime'
import {
  calculateConversion,
  categoryByKey,
  convertUnit,
  formatConvertedNumber,
  unitCategories,
  unitByKey,
  type ConverterSelection,
  type UnitCategoryKey,
  type UnitKey,
} from '@/shared/unit-converter'
import {
  pairByRouteKey,
  unitConverterRoute,
  unitConverterPairRoutes,
  type UnitConverterRouteKey,
} from '@/shared/unit-converter-routes'

interface UnitConverterPageProps {
  routeKey: UnitConverterRouteKey
}

type CopyStatus = 'idle' | 'copied' | 'error'

const broadSelection: ConverterSelection = {
  category: 'length',
  fromUnit: 'cm',
  toUnit: 'm',
}

export function UnitConverterPage({ routeKey }: UnitConverterPageProps) {
  const pair = pairByRouteKey(routeKey)
  const preset = useMemo<ConverterSelection>(() => pair ? {
    category: pair.category,
    fromUnit: pair.fromUnit,
    toUnit: pair.toUnit,
  } : broadSelection, [pair])
  const [selection, setSelection] = useState<ConverterSelection>(preset)
  const [input, setInput] = useState('')
  const [copyStatus, setCopyStatus] = useState<CopyStatus>('idle')
  const lastAnalyticsSnapshot = useRef<string | null>(null)

  const category = categoryByKey(selection.category)
  const calculationRun = useMemo(() => {
    const startMs = performance.now()
    const state = calculateConversion(input, selection)
    return { state, durationMs: performance.now() - startMs }
  }, [input, selection])
  const calculation = calculationRun.state
  const route = routeKey === 'unit-converter'
    ? unitConverterRoute
    : unitConverterPairRoutes.find((item) => item.routeKey === routeKey)!
  const analyticsContext = useMemo<ToolAnalyticsContext>(() => ({
    app: 'calcdeck',
    domain: 'calcdeck.ru',
    route: route.path,
    utility: route.analyticsKey,
    processingMode: 'local',
  }), [route.analyticsKey, route.path])

  useEffect(() => {
    if (!calculation.snapshot || calculation.snapshot === lastAnalyticsSnapshot.current) return

    const timeout = window.setTimeout(() => {
      if (!calculation.snapshot || calculation.snapshot === lastAnalyticsSnapshot.current) return
      lastAnalyticsSnapshot.current = calculation.snapshot

      if (calculation.status === 'ready') {
        startToolOperation(analyticsContext).success(calculationRun.durationMs)
      } else if (calculation.error) {
        trackToolRejection(analyticsContext, 'invalid_input')
      }
    }, 450)

    return () => window.clearTimeout(timeout)
  }, [analyticsContext, calculation.error, calculation.snapshot, calculation.status, calculationRun.durationMs])

  const updateCategory = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    const nextCategory = event.target.value as UnitCategoryKey
    const units = categoryByKey(nextCategory).units
    setSelection({ category: nextCategory, fromUnit: units[0]!.key, toUnit: units[1]!.key })
    setCopyStatus('idle')
  }, [])

  const updateUnit = useCallback((side: 'fromUnit' | 'toUnit') => (event: ChangeEvent<HTMLSelectElement>) => {
    setSelection((current) => ({ ...current, [side]: event.target.value as UnitKey }))
    setCopyStatus('idle')
  }, [])

  const swap = useCallback(() => {
    setSelection((current) => ({
      ...current,
      fromUnit: current.toUnit,
      toUnit: current.fromUnit,
    }))
    setCopyStatus('idle')
  }, [])

  const reset = useCallback(() => {
    setSelection(preset)
    setInput('')
    setCopyStatus('idle')
    lastAnalyticsSnapshot.current = null
  }, [preset])

  const copyResult = useCallback(async () => {
    const target = unitByKey(selection.category, selection.toUnit)
    if (!calculation.formatted || !target || !navigator.clipboard) {
      setCopyStatus('error')
      return
    }

    try {
      await navigator.clipboard.writeText(`${calculation.formatted} ${target.abbreviation}`)
      setCopyStatus('copied')
    } catch {
      setCopyStatus('error')
    }
  }, [calculation.formatted, selection])

  const sourceUnit = unitByKey(selection.category, selection.fromUnit)
  const targetUnit = unitByKey(selection.category, selection.toUnit)

  return (
    <div className="mx-auto max-w-6xl">
      <section className="overflow-hidden rounded-lg border bg-card text-card-foreground shadow">
        <div aria-hidden="true" className="h-1.5 bg-gradient-to-r from-teal-600 via-amber-400 to-red-500" />
        <div className="grid min-w-0 gap-0 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.86fr)]">
          <div className="min-w-0 space-y-3 border-b p-4 sm:space-y-5 sm:p-6 lg:border-b-0 lg:border-r">
            <SelectField id="unit-category" label="Категория" value={selection.category} onChange={updateCategory}>
              {unitCategories.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
            </SelectField>

            <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-end gap-2 sm:gap-3">
              <SelectField id="from-unit" label="Из единицы" value={selection.fromUnit} onChange={updateUnit('fromUnit')}>
                {category.units.map((unit) => <option key={unit.key} value={unit.key}>{unit.label} ({unit.abbreviation})</option>)}
              </SelectField>
              <Button type="button" variant="outline" onClick={swap} className="px-2.5 sm:px-4" aria-label="Поменять единицы местами">
                <span className="hidden sm:inline">Поменять </span>↔
              </Button>
              <SelectField id="to-unit" label="В единицу" value={selection.toUnit} onChange={updateUnit('toUnit')}>
                {category.units.map((unit) => <option key={unit.key} value={unit.key}>{unit.label} ({unit.abbreviation})</option>)}
              </SelectField>
            </div>

            <label htmlFor="unit-source-value" className="block min-w-0">
              <span className="mb-2 block text-sm font-medium">Исходное значение</span>
              <div className="relative min-w-0">
                <input
                  id="unit-source-value"
                  inputMode="decimal"
                  value={input}
                  onChange={(event) => {
                    setInput(event.target.value)
                    setCopyStatus('idle')
                  }}
                  placeholder="Например, 12,5"
                  aria-invalid={calculation.status === 'invalid'}
                  aria-describedby={calculation.status === 'invalid' ? 'unit-input-error' : undefined}
                  className={cn(
                    'h-12 w-full min-w-0 rounded-md border bg-background px-3 pr-20 font-mono text-lg tabular-nums outline-none transition-colors',
                    'focus:border-primary focus:ring-2 focus:ring-ring/20',
                    calculation.status === 'invalid' && 'border-destructive focus:border-destructive focus:ring-destructive/20',
                  )}
                />
                <span className="pointer-events-none absolute inset-y-0 right-3 flex max-w-16 items-center truncate text-sm text-muted-foreground">
                  {sourceUnit?.abbreviation}
                </span>
              </div>
            </label>

            {calculation.status === 'invalid' && (
              <p id="unit-input-error" role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {calculation.error === 'units_match'
                  ? 'Выберите разные исходную и целевую единицы.'
                  : 'Введите конечное число с точкой или запятой, например 12,5.'}
              </p>
            )}

          </div>

          <div aria-live="polite" className="flex min-w-0 flex-col bg-background/55 p-4 sm:p-6 lg:min-h-[350px]">
            <div className="text-sm font-medium text-muted-foreground">Результат</div>
            {calculation.status === 'ready' && calculation.formatted ? (
              <div className="mt-4 min-w-0">
                <div className="min-w-0 rounded-md border border-primary/35 bg-card p-4 shadow-sm sm:p-5">
                  <div className="break-words font-mono text-3xl font-semibold tabular-nums sm:text-4xl">
                    {calculation.formatted} <span className="text-xl text-muted-foreground sm:text-2xl">{targetUnit?.abbreviation}</span>
                  </div>
                </div>
                <div className="mt-5 rounded-md border bg-card p-4">
                  <div className="text-xs font-medium uppercase text-muted-foreground">Соотношение</div>
                  <p className="mt-2 break-words font-mono text-sm">
                    1 {sourceUnit?.abbreviation} = {formatConvertedNumber(convertUnit(1, selection))} {targetUnit?.abbreviation}
                  </p>
                </div>
              </div>
            ) : (
              <div className="mt-4 rounded-md border bg-card p-4 shadow-sm">
                <div className="font-semibold">{calculation.status === 'invalid' ? 'Проверьте значение' : 'Введите значение'}</div>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                  {calculation.status === 'invalid'
                    ? 'Старый результат очищен. После исправления он обновится автоматически.'
                    : 'Результат появится автоматически, без отдельной кнопки расчёта.'}
                </p>
              </div>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {calculation.formatted && <Button variant="outline" onClick={copyResult}>Скопировать результат</Button>}
              <Button variant="ghost" onClick={reset}>Сбросить</Button>
              {copyStatus === 'copied' && <span className="text-sm text-primary">Результат скопирован.</span>}
              {copyStatus === 'error' && <span role="alert" className="text-sm text-destructive">Не удалось скопировать результат.</span>}
            </div>
          </div>
        </div>
      </section>

      {pair && (
        <section className="mt-6 rounded-lg border bg-card p-5 shadow-sm">
          <h2 className="text-lg font-semibold">Формула: {pair.shortTitle}</h2>
          <p className="mt-2 break-words font-mono text-sm">{pair.formula}</p>
          <p className="mt-2 text-sm text-muted-foreground">Пример: {pair.example}.</p>
          <a href={unitConverterPairsPath(pair.reversePairKey)} className="mt-4 inline-block text-sm font-medium text-primary underline-offset-4 hover:underline">
            Обратный перевод
          </a>
        </section>
      )}

      <CommonConversions selection={selection} />
    </div>
  )
}

function unitConverterPairsPath(pairKey: string): string {
  return unitConverterPairRoutes.find((route) => route.routeKey === `unit-converter-${pairKey}`)?.path ?? unitConverterRoute.path
}

function SelectField({ id, label, value, onChange, children }: {
  id: string
  label: string
  value: string
  onChange: (event: ChangeEvent<HTMLSelectElement>) => void
  children: React.ReactNode
}) {
  return (
    <label htmlFor={id} className="block min-w-0">
      <span className="mb-2 block truncate text-sm font-medium" title={label}>{label}</span>
      <select
        id={id}
        value={value}
        onChange={onChange}
        className="h-11 w-full min-w-0 rounded-md border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-ring/20"
      >
        {children}
      </select>
    </label>
  )
}

function CommonConversions({ selection }: { selection: ConverterSelection }) {
  const category = categoryByKey(selection.category)
  const from = unitByKey(selection.category, selection.fromUnit)
  const rows = category.units
    .filter((unit) => unit.key !== selection.fromUnit)
    .slice(0, 4)

  return (
    <section className="mt-8">
      <h2 className="text-xl font-semibold">Частые переводы: {category.label.toLowerCase()}</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {rows.map((unit) => (
          <Card key={unit.key} className="min-w-0 rounded-lg">
            <CardContent className="min-w-0 pt-6">
              <div className="break-words font-mono text-lg font-semibold tabular-nums">
                1 {from?.abbreviation} = {formatConvertedNumber(convertUnit(1, { ...selection, toUnit: unit.key }))} {unit.abbreviation}
              </div>
              <p className="mt-2 text-sm text-muted-foreground">{from?.label} → {unit.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  )
}
